import { useEffect, useState } from 'react';
import { INTEGRATIONS, type SecretRow } from 'shared';
import { api } from '../api';
import { alert, notify } from '../telegram';

/**
 * Токены и ключи площадки.
 *
 * Экран заведён затем, чтобы софт можно было передать другому заказчику:
 * он подключает своего бота, свои ключи и свою почту сам, не заходя на
 * сервер. Раньше это лежало в `.env` — то есть либо разработчик в проекте
 * навсегда, либо чужие руки в файле с секретами на боевой машине.
 *
 * Значения секретных полей сюда не приходят никогда, даже
 * администратору: видно только хвост, источник и кто менял. Заменить
 * токен можно, не читая его, а лишний путь наружу у секрета лишним и
 * остаётся.
 */

const SOURCE_LABEL: Record<SecretRow['source'], string> = {
  db: 'задано здесь',
  env: 'из настроек сервера',
  none: 'не задано',
};

export default function AdminIntegrations() {
  const [rows, setRows] = useState<SecretRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setRows((await api.secrets()).items);
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save(key: string) {
    const value = drafts[key] ?? '';
    setBusy(key);
    try {
      await api.setSecret(key, value);
      // Черновик убираем: поле снова показывает состояние, а не то, что
      // в нём набирали. Оставленный текст выглядел бы как «не сохранилось».
      setDrafts((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      await load();
      notify('success');
    } catch (e) {
      notify('error');
      await alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <div className="empty">Загрузка…</div>;

  const byKey = new Map(rows.map((r) => [r.key, r]));
  const groups = [...new Set(INTEGRATIONS.map((f) => f.group))];

  return (
    <>
      {/* Заголовка нет: название раздела рисует сама админка, и второй
          такой же под ним читается как отдельный экран внутри экрана. */}
      <div className="meta" style={{ marginBottom: 'var(--s3)' }}>
        Всё, что нужно подключить площадке, задаётся здесь — заходить на
        сервер не требуется. Секретные значения не показываются: видно
        только последние знаки, чтобы отличить один ключ от другого.
        Пустое поле сохраняет «сбросить» — параметр вернётся к значению из
        настроек сервера, если оно там есть.
      </div>

      {groups.map((group) => (
        <div className="card" key={group}>
          <h3>{group}</h3>

          {INTEGRATIONS.filter((f) => f.group === group).map((f) => {
            const row = byKey.get(f.key);
            const draft = drafts[f.key];
            const dirty = draft !== undefined;

            return (
              <div className="secretrow" key={f.key}>
                <div className="secretrow__head">
                  <strong>{f.title}</strong>
                  {f.required && !row?.filled && (
                    <span className="badge badge--warn">обязателен</span>
                  )}
                  <span className={row?.filled ? 'secretrow__state' : 'meta'}>
                    {row?.filled ? row.preview : SOURCE_LABEL.none}
                  </span>
                </div>

                <div className="meta">{f.hint}</div>

                <div className="secretrow__edit">
                  <input
                    type={f.secret ? 'password' : 'text'}
                    autoComplete="off"
                    placeholder={
                      row?.filled ? 'Заменить значение' : (f.placeholder ?? 'Не задано')
                    }
                    value={draft ?? ''}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [f.key]: e.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="secretrow__save"
                    disabled={!dirty || busy === f.key}
                    onClick={() => void save(f.key)}
                  >
                    {busy === f.key ? 'Сохраняю…' : 'Сохранить'}
                  </button>
                </div>

                {row?.filled && (
                  <div className="meta">
                    {SOURCE_LABEL[row.source]}
                    {row.updatedByName && ` · менял ${row.updatedByName}`}
                    {row.source === 'db' && (
                      <>
                        {' · '}
                        <button
                          type="button"
                          className="linkish"
                          disabled={busy === f.key}
                          onClick={() => {
                            setDrafts((d) => ({ ...d, [f.key]: '' }));
                            void save(f.key);
                          }}
                        >
                          сбросить
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Про то, что здесь настроить нельзя, сказано прямо: искать
          несуществующее поле человек будет дольше, чем читать эту строку. */}
      <div className="card">
        <h3>Что задаётся только на сервере</h3>
        <div className="meta">
          Подключение к базе, порт, каталог файлов и ключ подписи сессий
          нужны раньше, чем база отвечает, — они остаются в настройках
          сервера. Там же логин и пароль первого администратора: он
          создаётся до того, как в эту админку есть кому войти. Ключ карт
          вшивается в приложение при сборке, поэтому меняется вместе с
          выкладкой, а не отсюда.
        </div>
      </div>
    </>
  );
}
